import {
	Flex,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import PersistingTabIndex from "../components/PersistingTabIndex";
import ServerSideList, {
	type ServerSideListElementProps,
} from "../components/list/ServerSideList";
import {
	useMarkAsRead,
	useMarkAsUnread,
	useNotificationsInfinite,
} from "../hooks/notifications.hooks";
import type { Notification } from "../types/notifications.types";
import { NavLink, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import BellIcon from "../components/icons/BellIcon";
import { getLongExposedId } from "../services/command.service";
import Select from "../components/inputs/Select";
import { useState } from "react";

const getNotificationAccessPath = (notification: Notification) => {
	if (
		notification.type === "NEW_TICKET" ||
		notification.type === "NEW_TICKET_COMMENT"
	) {
		return notification.ticket?.id
			? `/tickets/${notification.ticket?.id}`
			: undefined;
	}
	if (notification.type === "ACCOUNT_CREATION") {
		const notificationMeta = notification.meta as { userId?: number };
		return notificationMeta?.userId
			? `/contacts/users/${notificationMeta.userId}`
			: undefined;
	}
	return undefined;
};

function NotificationPageCardEmpty() {
	return (
		<Flex
			direction="column"
			justify="center"
			alignItems="center"
			backgroundColor="white"
			borderRadius="xl"
			p="24px"
			m="4px"
			h="min(40vh, 400px)"
		>
			<Flex
				w="48px"
				h="48px"
				bg="zinc.50"
				border="solid 1px"
				borderColor="brand.100"
				justifyContent="center"
				alignItems="center"
				borderRadius={100}
				mr="8px"
			>
				<BellIcon w="24px" h="24px" color="brand.700"></BellIcon>
			</Flex>
			<Text lineHeight="30px" fontSize="20px" fontWeight="600" color="gray.900">
				Aucune notification
			</Text>
			<Text lineHeight="30px" fontSize="12px" fontWeight="200" color="gray.400">
				Vous pouvez essayer avec une autre recherche
			</Text>
		</Flex>
	);
}

function NotificationPageCard({ data }: ServerSideListElementProps) {
	const notification = data as Notification;

	const NotificationPageCardNewTicket = () => {
		const markAsRead = useMarkAsRead();
		const markAsUnread = useMarkAsUnread();

		return (
			<Flex justify="space-between">
				<Flex direction="column">
					<Flex>
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
						>
							Nouveau ticket
						</Text>
						<NavLink to={`/tickets/${notification.ticket?.id}`}>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="600"
								color="brand.700"
								ml={4}
							>
								#{notification.ticket?.id}
							</Text>
						</NavLink>
					</Flex>
					<Flex>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
						>
							{notification.createdAt
								? format(notification.createdAt, "HH:mm dd/MM/yyyy - ")
								: ""}
						</Text>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
							textTransform="uppercase"
						>
							&nbsp;
							{notification.center ? `${notification.center.name}` : ""}
						</Text>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
							textTransform="uppercase"
						>
							&nbsp;
							{notification.command
								? getLongExposedId(notification.command)
								: ""}
						</Text>
					</Flex>
				</Flex>
				<Flex gap={4}>
					{notification.hasBeenRead === false && (
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
							_hover={{
								textDecoration: "underline",
								cursor: "pointer",
							}}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								markAsRead({ id: notification.id });
							}}
						>
							Marquer comme lue
						</Text>
					)}
					{notification.hasBeenRead === true && (
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
							_hover={{
								textDecoration: "underline",
								cursor: "pointer",
							}}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								markAsUnread({ id: notification.id });
							}}
						>
							Marquer comme non lue
						</Text>
					)}
					<Text
						lineHeight="21px"
						fontSize="14px"
						fontWeight="600"
						color="brand.800"
						_hover={{
							textDecoration: "underline",
							cursor: "pointer",
						}}
					>
						Accéder
					</Text>
				</Flex>
			</Flex>
		);
	};
	const NotificationPageCardNewTicketComment = () => {
		const markAsRead = useMarkAsRead();
		const markAsUnread = useMarkAsUnread();

		return (
			<Flex justify="space-between">
				<Flex direction="column">
					<Flex>
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
						>
							Nouveau message sur le ticket
						</Text>
						<NavLink to={`/tickets/${notification.ticket?.id}`}>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="600"
								color="brand.700"
								ml={4}
							>
								#{notification.ticket?.id}
							</Text>
						</NavLink>
					</Flex>
					<Flex>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
						>
							{notification.createdAt
								? format(notification.createdAt, "HH:mm dd/MM/yyyy - ")
								: ""}
						</Text>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
							textTransform="uppercase"
						>
							&nbsp;
							{notification.center ? `${notification.center.name} - ` : ""}
						</Text>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
							textTransform="uppercase"
						>
							&nbsp;
							{notification.command?.exposedId
								? `${notification.command?.exposedId}`
								: ""}
						</Text>
					</Flex>
				</Flex>
				<Flex gap={4}>
					{notification.hasBeenRead === false && (
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
							_hover={{
								textDecoration: "underline",
								cursor: "pointer",
							}}
							onClick={(event) => {
								event.stopPropagation();
								markAsRead({ id: notification.id });
							}}
						>
							Marquer comme lue
						</Text>
					)}
					{notification.hasBeenRead === true && (
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
							_hover={{
								textDecoration: "underline",
								cursor: "pointer",
							}}
							onClick={(event) => {
								event.stopPropagation();
								markAsUnread({ id: notification.id });
							}}
						>
							Marquer comme non lue
						</Text>
					)}
					<Text
						lineHeight="21px"
						fontSize="14px"
						fontWeight="600"
						color="brand.800"
						_hover={{
							textDecoration: "underline",
							cursor: "pointer",
						}}
					>
						Accéder
					</Text>
				</Flex>
			</Flex>
		);
	};
	const NotificationPageCardAccountCreationComment = () => {
		const markAsRead = useMarkAsRead();
		const markAsUnread = useMarkAsUnread();
		const notificationMeta = notification.meta as {
			userId: number;
			firstName: string;
			lastName: string;
		};

		return (
			<Flex justify="space-between">
				<Flex direction="column">
					<Flex>
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
						>
							Demande de création de compte
						</Text>
					</Flex>
					<Flex>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
						>
							{notification.createdAt
								? format(notification.createdAt, "HH:mm dd/MM/yyyy - ")
								: ""}
						</Text>
						<Text
							lineHeight="21px"
							fontSize="12px"
							fontWeight="100"
							color="gray.400"
							textTransform="uppercase"
						>
							&nbsp;
							{notificationMeta.firstName}
							&nbsp;
							{notificationMeta.lastName}
						</Text>
					</Flex>
				</Flex>
				<Flex gap={4}>
					{notification.hasBeenRead === false && (
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
							_hover={{
								textDecoration: "underline",
								cursor: "pointer",
							}}
							onClick={(event) => {
								event.stopPropagation();
								markAsRead({ id: notification.id });
							}}
						>
							Marquer comme lue
						</Text>
					)}
					{notification.hasBeenRead === true && (
						<Text
							lineHeight="21px"
							fontSize="14px"
							fontWeight="600"
							color="gray.800"
							_hover={{
								textDecoration: "underline",
								cursor: "pointer",
							}}
							onClick={(event) => {
								event.stopPropagation();
								markAsUnread({ id: notification.id });
							}}
						>
							Marquer comme non lue
						</Text>
					)}
					<Text
						lineHeight="21px"
						fontSize="14px"
						fontWeight="600"
						color="brand.800"
						_hover={{
							textDecoration: "underline",
							cursor: "pointer",
						}}
					>
						Accéder
					</Text>
				</Flex>
			</Flex>
		);
	};

	if (notification.type === "NEW_TICKET") {
		return <NotificationPageCardNewTicket />;
	}
	if (notification.type === "NEW_TICKET_COMMENT") {
		return <NotificationPageCardNewTicketComment />;
	}
	if (notification.type === "ACCOUNT_CREATION") {
		return <NotificationPageCardAccountCreationComment />;
	}
	return <></>;
}

type ReadFilter = "all" | "read" | "unread";

function NotificationsPageAll({ readFilter }: { readFilter: ReadFilter }) {
	const markAsRead = useMarkAsRead();
	const navigate = useNavigate();
	const query = useNotificationsInfinite({
		filters: {
			...(readFilter === "read"
				? { hasBeenRead: "true" }
				: readFilter === "unread"
					? { hasBeenRead: "false" }
					: {}),
		},
		sortBy: ["-createdAt"],
	});
	return (
		<>
			<ServerSideList
				query={query}
				template={NotificationPageCard}
				templateEmpty={NotificationPageCardEmpty}
				cardProps={({ data }) => {
					const notification = data as Notification;
					return {
						backgroundColor: notification.hasBeenRead ? "white" : "brand.100",
						cursor: "pointer",
						onClick: () => {
							const path = getNotificationAccessPath(notification);
							if (!path) {
								return;
							}
							markAsRead({ id: notification.id });
							navigate(path);
						},
					};
				}}
			/>
		</>
	);
}

function NotificationsPageTicketing({
	readFilter,
}: {
	readFilter: ReadFilter;
}) {
	const markAsRead = useMarkAsRead();
	const navigate = useNavigate();
	const query = useNotificationsInfinite({
		filters: {
			category: "TICKETS",
			...(readFilter === "read"
				? { hasBeenRead: "true" }
				: readFilter === "unread"
					? { hasBeenRead: "false" }
					: {}),
		},
		sortBy: ["-createdAt"],
	});
	return (
		<>
			<ServerSideList
				query={query}
				template={NotificationPageCard}
				templateEmpty={NotificationPageCardEmpty}
				cardProps={({ data }) => {
					const notification = data as Notification;
					return {
						backgroundColor: notification.hasBeenRead ? "white" : "brand.25",
						cursor: "pointer",
						onClick: () => {
							const path = getNotificationAccessPath(notification);
							if (!path) {
								return;
							}
							markAsRead({ id: notification.id });
							navigate(path);
						},
					};
				}}
			/>
		</>
	);
}

function NotificationsPageCommands({ readFilter }: { readFilter: ReadFilter }) {
	const markAsRead = useMarkAsRead();
	const navigate = useNavigate();
	const query = useNotificationsInfinite({
		filters: {
			category: "COMMANDS",
			...(readFilter === "read"
				? { hasBeenRead: "true" }
				: readFilter === "unread"
					? { hasBeenRead: "false" }
					: {}),
		},
		sortBy: ["-createdAt"],
	});
	return (
		<>
			<ServerSideList
				query={query}
				template={NotificationPageCard}
				templateEmpty={NotificationPageCardEmpty}
				cardProps={({ data }) => {
					const notification = data as Notification;
					return {
						backgroundColor: notification.hasBeenRead ? "white" : "brand.25",
						cursor: "pointer",
						onClick: () => {
							const path = getNotificationAccessPath(notification);
							if (!path) {
								return;
							}
							markAsRead({ id: notification.id });
							navigate(path);
						},
					};
				}}
			/>
		</>
	);
}

function NotificationsPageOther({ readFilter }: { readFilter: ReadFilter }) {
	const markAsRead = useMarkAsRead();
	const navigate = useNavigate();
	const query = useNotificationsInfinite({
		filters: {
			category: "OTHER",
			...(readFilter === "read"
				? { hasBeenRead: "true" }
				: readFilter === "unread"
					? { hasBeenRead: "false" }
					: {}),
		},
		sortBy: ["-createdAt"],
	});
	return (
		<>
			<ServerSideList
				query={query}
				template={NotificationPageCard}
				templateEmpty={NotificationPageCardEmpty}
				cardProps={({ data }) => {
					const notification = data as Notification;
					return {
						backgroundColor: notification.hasBeenRead ? "white" : "brand.25",
						cursor: "pointer",
						onClick: () => {
							const path = getNotificationAccessPath(notification);
							if (!path) {
								return;
							}
							markAsRead({ id: notification.id });
							navigate(path);
						},
					};
				}}
			/>
		</>
	);
}

function NotificationsPage() {
	const [readFilter, setReadFilter] = useState<ReadFilter>("all");

	return (
		<>
			<PageTitle pb={10}>Notifications</PageTitle>

			<Flex mb={6} justify="flex-end">
				<Select
					placeholder="Statut"
					noEmpty
					value={readFilter}
					onChange={(event) => setReadFilter(event.target.value as ReadFilter)}
					w="220px"
				>
					<option value="all">Toutes</option>
					<option value="read">Lues</option>
					<option value="unread">Non lues</option>
				</Select>
			</Flex>

			<PersistingTabIndex id={"notifications"}>
				<TabList>
					<Tab
						_selected={{
							color: "brand.700",
							borderColor: "brand.700",
						}}
					>
						Toutes
					</Tab>
					<Tab
						_selected={{
							color: "brand.700",
							borderColor: "brand.700",
						}}
					>
						Ticketing
					</Tab>
					<Tab
						_selected={{
							color: "brand.700",
							borderColor: "brand.700",
						}}
					>
						Commandes
					</Tab>
					<Tab
						_selected={{
							color: "brand.700",
							borderColor: "brand.700",
						}}
					>
						Autre
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						<NotificationsPageAll
							key={`notifications-all-${readFilter}`}
							readFilter={readFilter}
						/>
					</TabPanel>
					<TabPanel px={0}>
						<NotificationsPageTicketing
							key={`notifications-tickets-${readFilter}`}
							readFilter={readFilter}
						/>
					</TabPanel>
					<TabPanel px={0}>
						<NotificationsPageCommands
							key={`notifications-commands-${readFilter}`}
							readFilter={readFilter}
						/>
					</TabPanel>
					<TabPanel px={0}>
						<NotificationsPageOther
							key={`notifications-other-${readFilter}`}
							readFilter={readFilter}
						/>
					</TabPanel>
				</TabPanels>
			</PersistingTabIndex>
		</>
	);
}

export default NotificationsPage;
